import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { connectSocket, getSocket } from '@/lib/socket/socket';
import {
  notificationKeys,
  useNotifications,
  useUnreadCount,
  type NotificationListResponse,
  type NotificationPayload,
} from '@/hooks/useNotifications';

interface NotificationsContextValue {
  notifications: NotificationPayload[];
  unreadCount: number;
  isLoading: boolean;
}

const NotificationsContext = React.createContext<NotificationsContextValue | null>(null);

/**
 * Opens the authenticated notifications socket and bridges live events into the
 * React Query cache (the REST inbox remains the source of truth; the socket only
 * carries deltas). Mount inside the authenticated app tree — never on public
 * routes, so `/login` never opens a socket. Teardown happens on logout via
 * `disconnectSocket()` in `useAuth`, not on unmount.
 */
export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { data: listData, isLoading } = useNotifications();
  const { data: unreadCount } = useUnreadCount();

  React.useEffect(() => {
    connectSocket();
    const socket = getSocket();

    const onNotification = (n: NotificationPayload) => {
      queryClient.setQueryData<NotificationListResponse>(notificationKeys.list(), (prev) => {
        const items = prev?.items ?? [];
        if (items.some((existing) => existing.id === n.id)) return prev; // de-dupe fan-out
        return { items: [n, ...items], nextCursor: prev?.nextCursor ?? null };
      });

      // Only user-level rows carry a persisted unread badge in v1 (org-level read
      // state would need a per-user join table — documented follow-up).
      if (n.userId != null) {
        queryClient.setQueryData<number>(notificationKeys.unreadCount(), (count) =>
          typeof count === 'number' ? count + 1 : 1,
        );
      }

      toast(n.title, n.body ? { description: n.body } : undefined);
    };

    socket.on('notification', onNotification);
    return () => {
      socket.off('notification', onNotification);
    };
  }, [queryClient]);

  const value = React.useMemo<NotificationsContextValue>(
    () => ({
      notifications: listData?.items ?? [],
      unreadCount: unreadCount ?? 0,
      isLoading,
    }),
    [listData, unreadCount, isLoading],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = React.useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotificationsContext must be used within a NotificationsProvider');
  }
  return ctx;
}
